import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Stack,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Snackbar,
  Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import FileUpload from "./file-upload/file-upload";
import { schemaHelper } from "../../utils/schemaHelper";
import { uploadFileToS3 } from "../../utils/s3/actions";
import { PageContext } from "../../pages/job-details";
import { Divider } from "@mui/material";
import { applyJob } from "../../controllers/job-controllers";
import PhoneInput from "react-phone-number-input";
import { isValidPhoneNumber } from "react-phone-number-input/input";
import "react-phone-number-input/style.css";
import { config } from "../../config";
import { useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { allowedFileTypes } from "../../constants/enum";
import { uploadFileToChatgpt } from "../../controllers/chatgpt";
import { useTranslate } from "../../locales/use-locales";

function ApplicationForm({
  isPreview = false,
  jobId,
  jobData,
  hideApply = false,
}) {
  const { t } = useTranslate();
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false); // Add loading state
  const [statusMessage, setStatusMessage] = useState("");
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const platform = searchParams.get("platform");

  const [state, setState] = React.useState({
    open: false,
    vertical: "bottom",
    horizontal: "right",
    errorState: false,
  });
  const { vertical, horizontal, open, errorState } = state;

  // const { jobData } = useContext(PageContext)

  const schema = z.object({
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
    email: z.string().email({ message: "Invalid email address" }),
    phone: z
      .string()
      .min(1, { message: "Phone number is required!" })
      .refine(
        (value) => isValidPhoneNumber(value), // Use the `isValidPhoneNumber` function from react-phone-number-input
        { message: "Invalid phone number" }
      ),
    yearsOfExperience: jobData?.candidateRequirements?.includes(
      "YEAR OF EXPERIENCE"
    )
      ? z
          .number()
          .min(0, { message: "Years of experience is required" })
          .max(50, { message: "Years of experience cannot exceed 50" })
      : z.any().optional(),
    acceptTerms: z.boolean().refine((val) => val === true, {
      message: t("errors.termsRequired"),
    }),
    // Conditionally require the CV field based on isCvRequired
    cv: jobData?.candidateRequirements?.includes("UPLOAD CV")
      ? schemaHelper.file({
          message: { required_error: t("errors.cvRequired") },
          validate: (file) => {
            if (!file) return true;
            const allowedTypes = allowedFileTypes;
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (file.size > maxSize) {
              return t("errors.fileSizeLimit");
            }
            return (
              allowedTypes.includes(file.type) || t("errors.allowedFileTypes")
            );
          },
        })
      : z.any().optional(),
    picture: jobData?.candidateRequirements?.includes("PICTURE")
      ? schemaHelper.file({
          message: { required_error: t("errors.pictureRequired") },
          validate: (file) => {
            if (!file) return true;
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (file.size > maxSize) {
              return t("errors.fileSizeLimit");
            }
            return true;
          },
        })
      : z.any().optional(),
    personalLetter: jobData?.candidateRequirements?.includes("PERSONAL LETTER")
      ? schemaHelper.file({
          message: { required_error: t("errors.personalLetterRequired") },
          validate: (file) => {
            if (!file) return true;
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (file.size > maxSize) {
              return t("errors.fileSizeLimit");
            }
            return true;
          },
        })
      : z.any().optional(),
  });

  const textFieldStyles = {
    borderRadius: "8px",
    backgroundColor: "white",
    color: "black",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "transparent", // No border in default state
      },
      "&:hover fieldset": {
        borderColor: "transparent", // No border on hover
      },
      "&.Mui-focused fieldset": {
        borderWidth: "1px", // Keep border width constant on focus
        borderColor: "transparent", // No border color change on focus
      },
      "& input:-webkit-autofill": {
        WebkitBoxShadow: "0 0 0 1000px white inset", // Keep background white on autofill
        WebkitTextFillColor: "black", // Text color black on autofill
      },
    },
  };

  const defaultValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    picture: "",
    // yearsOfExperience: 0,
    cv: "",
    personalLetter: "",
    acceptTerms: false,
  };

  // console.log(jobData)
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue, // This will allow us to update the `cv` field with file input
    trigger, // Trigger validation manually
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [errors]);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [pictures, setPictures] = useState([]);

  function convertFileName(fileName) {
    // Extract the file extension
    const fileExtension = fileName.split(".").pop();

    // Remove the extension from the file name
    const baseName = fileName.replace(`.${fileExtension}`, "");

    // Remove unwanted characters and keep letters, numbers, and spaces
    const cleanedBaseName = baseName
      .replace(/[^a-zA-Z0-9]/g, "") // Keep only letters and numbers
      .toLowerCase(); // Optional: Convert to lowercase

    // Reattach the file extension
    return `${cleanedBaseName}-${generateRandomId(5)}.${fileExtension}`;
  }

  function generateRandomId(length = 8) {
    const characters =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    const charactersLength = characters.length;

    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }

    return result;
  }

  const onSubmit = async (data) => {
    // Handle the validation of the non zod fields
    let validation = handleValidation();

    if (!validation) return;

    if (isPreview) {
      let positions = { vertical: "bottom", horizontal: "right" };
      setState({ ...positions, open: true, errorState: true });
      setStatusMessage(
        "Detta är bara en preview du kan inte ansöka till jobbet!"
      );
      return;
    }

    const isValid = await trigger();
    setLoading(true);
    setStatusMessage("");

    if (!isValid) {
      setStatusMessage("Fill in all required fields.");
      setLoading(false);
      return;
    }

    try {
      let cv_link = "";
      let picture_link = "";
      let personalLetter_link = "";

      const file = data.cv && data.cv[0];
      const picture = data.picture && data.picture[0];
      const personalLetter = data.personalLetter && data.personalLetter[0];
      let openAifileId = "";

      // Build promises for signed URL requests
      const signedUrlRequests = [];

      if (file) {
        signedUrlRequests.push(
          fetch(`${process.env.REACT_APP_SERVER_URL}/user/website/job/upload`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileName: convertFileName(file.name),
              fileType: file.type,
              path: `jobs/${jobId}/cvs`,
            }),
          })
        );
        openAifileId = await uploadFileToChatgpt(file);
      }

      if (picture) {
        signedUrlRequests.push(
          fetch(`${process.env.REACT_APP_SERVER_URL}/user/website/job/upload`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileName: convertFileName(picture.name),
              fileType: picture.type,
              path: `jobs/${jobId}/pictures`,
            }),
          })
        );
      }

      if (personalLetter) {
        signedUrlRequests.push(
          fetch(`${process.env.REACT_APP_SERVER_URL}/user/website/job/upload`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              fileName: convertFileName(personalLetter.name),
              fileType: personalLetter.type,
              path: `jobs/${jobId}/personal-letters`,
            }),
          })
        );
      }
      const signedUrlResponses = await Promise.all(signedUrlRequests);

      // Validate responses
      signedUrlResponses.forEach((res) => {
        if (!res.ok) throw new Error("Failed to get signed URL");
      });

      // Get the signed URLs from the responses
      const [
        cvSignedUrlData,
        pictureSignedUrlData,
        personalLetterSignedUrlData,
      ] = await Promise.all(signedUrlResponses.map((res) => res.json()));

      // Build upload promises
      const uploadPromises = [];

      if (file && cvSignedUrlData?.url) {
        uploadPromises.push(uploadFileToS3(file, cvSignedUrlData.url));
      }

      if (picture && pictureSignedUrlData?.url) {
        uploadPromises.push(uploadFileToS3(picture, pictureSignedUrlData.url));
      }

      if (personalLetter && personalLetterSignedUrlData?.url) {
        uploadPromises.push(
          uploadFileToS3(personalLetter, personalLetterSignedUrlData.url)
        );
      }

      const [cvUploadResult, pictureUploadResult, personalLetterUploadResult] =
        await Promise.all(uploadPromises);

      // Assign links
      cv_link = cvUploadResult || "";
      picture_link = pictureUploadResult || "";
      personalLetter_link = personalLetterUploadResult || "";

      // Build the application data, including the CV link
      const data_to_send = {
        applicantId: "",
        drivingLicense: "",
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        isApplicantAccountExist: false,
        jobId: jobId,
        otherDocuments: [],
        overide: true,
        // personalLetter: data.personalLetter,
        phoneNumber: data.phone,
        platformSource: "",
        questions: questions,
        resume: cv_link,
        picture: picture_link,
        yearOfExperience: data.yearsOfExperience ?? null,
        openAifileId,
        personalLetter: personalLetter_link,
      };

      // Now that the file upload is complete, submit the job application
      const res = await applyJob(data_to_send, platform);

      if (res.error) {
        let positions = { vertical: "bottom", horizontal: "right" };
        setState({ ...positions, open: true, errorState: true });
        setStatusMessage(
          res.error == "job_application_exists"
            ? t("errors.alreadyApplied")
            : t("errors.submissionFailed")
        );
        throw new Error(
          res.error == "job_application_exists"
            ? t("errors.alreadyApplied")
            : t("errors.submissionFailed")
        );
      } else {
        // Optionally, reset or navigate upon success
        // reset();
        navigate("/thank-you");
      }
    } catch (error) {
      console.error(error);
      // setStatusMessage("Error submitting application. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setState({ ...state, open: false });
  };

  const handleCVUpload = (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (files[0]?.size > maxSize) {
      setState({ ...state, open: true, errorState: true });
      setStatusMessage(t("errors.fileSizeLimit"));
      return;
    }
    setSelectedFiles(files);
    setValue("cv", files);
    trigger("cv");
  };

  const onRemoveCV = () => {
    setSelectedFiles([]);
    setValue("cv", []);
    trigger("cv");
  };

  const handlePersonalLetterUpload = (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (files[0]?.size > maxSize) {
      setState({ ...state, open: true, errorState: true });
      setStatusMessage(t("errors.fileSizeLimit"));
      return;
    }
    setSelectedFiles(files);
    setValue("personalLetter", files);
    trigger("personalLetter");
  };

  const onRemovePersonalLetter = () => {
    setSelectedFiles([]);
    setValue("personalLetter", []);
    trigger("personalLetter");
  };

  const handlePictureUpload = (files) => {
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (files[0]?.size > maxSize) {
      setState({ ...state, open: true, errorState: true });
      setStatusMessage(t("errors.fileSizeLimit"));
      return;
    }
    setPictures(files);
    setValue("picture", files);
    trigger("picture");
  };

  const onRemovePicture = () => {
    setPictures([]);
    setValue("picture", []);
    trigger("picture");
  };

  useEffect(() => {
    // Initialize questions state with empty answers and modify the question structure
    if (jobData?.screeningQuestions?.questions) {
      setQuestions(
        jobData?.screeningQuestions?.questions.map((q) => ({
          question: { ...q, value: q.title }, // Add a 'value' key with q.title to the 'question'
          answer: [], // Initialize with an empty answer array
        }))
      );
    }
  }, [jobData?.screeningQuestions?.questions]);

  const handleValidation = () => {
    let validationErrors = {};

    questions.forEach((q, index) => {
      if (!q.answer || q.answer.length === 0) {
        validationErrors[index] = "This field is required";
      }
    });

    setError(validationErrors);

    // Return true if there are no validation errors
    return Object.keys(validationErrors).length === 0;
  };

  const handleAnswerChange = (q, value, index, choiceIndex = null) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      let currentQuestion = updatedQuestions[index];

      if (!currentQuestion.answer) {
        currentQuestion.answer = [];
      }

      // Handle multiple choice (checkbox) or radio
      if (choiceIndex !== null) {
        if (value) {
          // Add answer if checked (for checkboxes)
          currentQuestion.answer.push({
            value,
            weight: q.options[choiceIndex]?.weight || 0, // Use option's weight
          });
        } else {
          // Remove answer if unchecked (for checkboxes)
          currentQuestion.answer = currentQuestion.answer.filter(
            (answer) => answer.value !== q.options[choiceIndex].value
          );
        }
      } else {
        // Handle text input and single choice (radio buttons)
        if (q.type === "TEXT" && value.trim() === "") {
          // If it's a TEXT input and the value is empty, remove the answer
          currentQuestion.answer = [];
        } else if (q.type === "TEXT") {
          // Add or update the answer with weight 1 for text inputs
          currentQuestion.answer = [{ value, weight: 1 }];
        } else if (q.type === "CONDITIONAL" || q.type === "CHOICE") {
          // Handle radio button answers (single choice)
          const selectedOption = q.options.find(
            (option) => option.value === value
          );
          currentQuestion.answer = [
            {
              value,
              weight: selectedOption?.weight || 0, // Use selected option's weight
            },
          ];
        }
      }

      // Remove the error for this question if an answer is provided
      setError((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        if (currentQuestion.answer.length > 0) {
          delete updatedErrors[index];
        }
        return updatedErrors;
      });

      return updatedQuestions;
    });
  };

  const renderQuestionField = (q, index) => {
    return (
      <Grid size={12} key={index}>
        <Stack>
          <Typography sx={{ color: "white", fontWeight: "bold" }} mb={1}>
            {q.title}
          </Typography>
          {/* Render Text, Radio, or Checkbox based on type */}
          {q.type === "TEXT" && (
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Skriv här..."
              required
              onChange={(e) => handleAnswerChange(q, e.target.value, index)}
              sx={textFieldStyles}
            />
          )}

          {/* {console.log("choice", q)} */}
          {q.type === "CONDITIONAL" && (
            <RadioGroup
              onChange={(e) => handleAnswerChange(q, e.target.value, index)}
            >
              {q?.options.map((choice, i) => (
                <FormControlLabel
                  key={i}
                  value={choice.value}
                  sx={{ color: "white" }}
                  control={
                    <Radio
                      sx={{
                        color: "white",
                        "&.Mui-checked": {
                          color: "white",
                        },
                      }}
                    />
                  }
                  label={choice.value == "Yes" ? t("yes") : t("no")}
                />
              ))}
            </RadioGroup>
          )}

          {q.type === "CHOICE" && (
            <Stack>
              {q.isMultipleChoice ? (
                q?.options.map((choice, i) => (
                  <FormControlLabel
                    sx={{ color: "white" }}
                    key={i}
                    control={
                      <Checkbox
                        sx={{
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        }}
                        onChange={(e) =>
                          handleAnswerChange(
                            q,
                            e.target.checked ? choice.value : "",
                            index,
                            i
                          )
                        }
                      />
                    }
                    label={choice.value}
                  />
                ))
              ) : (
                <RadioGroup
                  onChange={(e) => handleAnswerChange(q, e.target.value, index)}
                >
                  {q?.options.map((choice, i) => (
                    <FormControlLabel
                      key={i}
                      value={choice.value}
                      sx={{ color: "white" }}
                      control={
                        <Radio
                          sx={{
                            color: "white",
                            "&.Mui-checked": {
                              color: "white",
                            },
                          }}
                        />
                      }
                      label={choice.value}
                    />
                  ))}
                </RadioGroup>
              )}
            </Stack>
          )}

          {/* Display error message if exists */}
          {error[index] && (
            <Typography mt={1} color="error" variant="body2">
              {error[index]}
            </Typography>
          )}
        </Stack>
      </Grid>
    );
  };

  return (
    <Container id={"apply"} component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* <Typography component="h1" variant="h5" sx={{color: 'white'}}>
          Application Form
        </Typography> */}

        <Box
          ref={formRef}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ mt: 3 }}
        >
          <Typography
            component="h1"
            variant="h5"
            sx={{ color: "white" }}
            fontWeight={"bold"}
            mb={3}
          >
            {t("application.title")}
          </Typography>
          <Grid container spacing={2}>
            {/* First Name */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ color: "white", mb: 1, fontWeight: "bold" }}>
                {t("application.firstName")}
              </Typography>
              <TextField
                required
                hiddenLabel
                fullWidth
                placeholder={t("application.firstName")}
                {...register("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName ? errors.firstName.message : ""}
                sx={textFieldStyles}
              />
            </Grid>

            {/* Last Name */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ color: "white", mb: 1, fontWeight: "bold" }}>
                {t("application.lastName")}
              </Typography>
              <TextField
                required
                fullWidth
                hiddenLabel
                placeholder={t("application.lastName")}
                {...register("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName ? errors.lastName.message : ""}
                sx={textFieldStyles}
              />
            </Grid>

            {/* Email */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ color: "white", mb: 1, fontWeight: "bold" }}>
                {t("application.email")}
              </Typography>
              <TextField
                required
                type="email"
                fullWidth
                hiddenLabel
                placeholder={t("application.email")}
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email ? errors.email.message : ""}
                sx={textFieldStyles}
              />
            </Grid>

            {/* Phone */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography sx={{ color: "white", mb: 1, fontWeight: "bold" }}>
                {t("application.phone")}
              </Typography>
              <PhoneInput
                value={watch("phone")}
                onChange={(e) => setValue("phone", e)}
                defaultCountry="SE"
                placeholder={t("application.phone")}
                style={{
                  backgroundColor: "white",
                  padding: "17px 14px",
                  borderRadius: "8px",
                  border: "none",
                  outline: "none",
                }}
              />
              {errors.phone && (
                <Typography mt={1} color="error" variant="body2">
                  {errors.phone.message}
                </Typography>
              )}
            </Grid>

            {/* Years of Experience */}
            {jobData?.candidateRequirements?.includes("YEAR OF EXPERIENCE") && (
              <Grid size={12}>
                <Typography sx={{ color: "white", mb: 1, fontWeight: "bold" }}>
                  {t("application.yearsOfExperience")}
                </Typography>
                <TextField
                  required
                  fullWidth
                  hiddenLabel
                  placeholder="Ex. 1"
                  type="number"
                  {...register("yearsOfExperience", { valueAsNumber: true })}
                  error={!!errors.yearsOfExperience}
                  helperText={
                    errors.yearsOfExperience
                      ? errors.yearsOfExperience.message
                      : ""
                  }
                  sx={textFieldStyles}
                />
              </Grid>
            )}

            {/* CV (File Upload) */}
            {jobData?.candidateRequirements?.includes("UPLOAD CV") && (
              <Grid size={12} mt={2}>
                <FileUpload
                  id={"cvupload"}
                  title={t("application.cv")}
                  allowMultiple={false}
                  onRemoveFile={onRemoveCV}
                  onFileChange={handleCVUpload}
                />
                {errors.cv && (
                  <Typography mt={1} color="error" variant="body2">
                    {errors.cv.message}
                  </Typography>
                )}
              </Grid>
            )}

            {/* PICTURE */}
            {jobData?.candidateRequirements?.includes("PICTURE") && (
              <Grid size={12} mt={2}>
                <FileUpload
                  id={"pictureupload"}
                  title={t("application.picture")}
                  allowMultiple={false}
                  onRemoveFile={onRemovePicture}
                  onFileChange={handlePictureUpload}
                />
                {errors.picture && (
                  <Typography mt={1} color="error" variant="body2">
                    {errors.picture.message}
                  </Typography>
                )}
              </Grid>
            )}

            {/* Personal Letter */}
            {jobData?.candidateRequirements?.includes("PERSONAL LETTER") && (
              <Grid size={12} mt={2}>
                <FileUpload
                  id={"personalLetterupload"}
                  title={t("application.personalLetter")}
                  allowMultiple={false}
                  onRemoveFile={onRemovePersonalLetter}
                  onFileChange={handlePersonalLetterUpload}
                />
                {/* <TextField
                  fullWidth
                  multiline
                  hiddenLabel
                  rows={4}
                  inputProps={{ maxLength: 500 }}
                  placeholder="Skriv här..."
                  {...register("personalLetter")}
                  error={!!errors.personalLetter}
                  helperText={
                    errors.personalLetter ? errors.personalLetter.message : ""
                  }
                  sx={textFieldStyles}
                /> */}
              </Grid>
            )}

            {jobData?.screeningQuestions?.questions &&
              jobData?.screeningQuestions?.questions.length > 0 && (
                <Grid size={12}>
                  <Divider
                    sx={{
                      marginTop: 5,
                      marginBottom: "20px!important",
                      borderColor: "white",
                      borderWidth: 1,
                    }}
                    flexItem
                  />
                </Grid>
              )}

            {jobData?.screeningQuestions?.questions &&
              jobData?.screeningQuestions?.questions.length > 0 &&
              jobData?.screeningQuestions?.questions.map((q, index) =>
                renderQuestionField(q, index)
              )}
          </Grid>
          <Grid size={12} marginTop={2}>
            <FormControlLabel
              control={
                <Checkbox
                  {...register("acceptTerms", {
                    required: t("errors.required"),
                  })}
                  sx={{
                    color: "white",
                    "&.Mui-checked": {
                      color: "white",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "white" }}>
                  {t("application.terms.accept")}{" "}
                  <a
                    style={{
                      color: "white",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                    href="https://sabinaai.com/terms"
                    target="_blank"
                  >
                    {t("application.terms.terms")}
                  </a>{" "}
                  {t("application.terms.forApplication")}
                </Typography>
              }
            />
            {errors.acceptTerms && (
              <Typography sx={{ color: "red", ml: 2 }}>
                {errors.acceptTerms.message}
              </Typography>
            )}
          </Grid>
          {!hideApply && (
            <Stack direction="row" justifyContent="flex-end">
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  borderRadius: "10px",
                  padding: "12px 25px",
                  fontWeight: "600",
                  width: "fit-content",
                  backgroundColor: "white",
                  color: "black",
                  transition: "background-color 500ms, color 500ms",
                  "&:hover": {
                    background: config.colors.secondary,
                    color: config.colors.white,
                  },
                  ":disabled": {
                    backgroundColor: "white",
                    opacity: 0.7,
                  },
                }}
                disabled={loading} // Disable button when loading
              >
                {loading
                  ? t("application.submitting")
                  : t("application.submit")}
              </Button>
            </Stack>
          )}

          <Snackbar
            autoHideDuration={6000}
            anchorOrigin={{ vertical, horizontal }}
            open={open}
          >
            <Alert
              onClose={handleClose}
              severity={errorState ? "error" : "success"}
              variant="filled"
              sx={{ width: "100%" }}
            >
              {errorState ? statusMessage : t("application.success")}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Container>
  );
}

export default ApplicationForm;
